<template>
  <div>
    <button class="material-add-button" @click="triggerFileInput">
      <img class="add-icon" :src="ADD_MATERIAL_ICON" alt="add icon" />
      <label class="addButton">
        <input
          ref="fileInputRef"
          type="file"
          :accept="FILE_INPUT_ACCEPT"
          multiple
          @change="callOnFileChange"
        />
      </label>
    </button>
    <v-ons-modal :visible="isModalVisible" class="modal-wrapper p-fixed">
      <ja-material-upload
        :updateIsMaterialUploadModalVisible="updateIsMaterialUploadModalVisible"
        :getMaterial="getMaterial"
        :uploadingMaterialObjectList="uploadingMaterialObjectList"
        :uploadingMaterialObject="uploadingMaterialObject"
        :materialFileList="materialFileList"
        :totalFileLength="totalFileLength"
        :totalFileSize="totalFileSize"
        :resetPollingInterval="resetPollingInterval"
      />
    </v-ons-modal>
    <div v-show="isProcessWait" class="progress-circular-wrapper">
      <progress-circular />
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import JaMaterialUpload from '@/components/JaContent/JaMaterialUpload.vue';
import ProgressCircular from '@/components/common/ProgressCircular.vue';
import { disableScroll, enableScroll, isSmartPhone } from '@/composable/common';
import { onFileChange } from '@/composable/material_common';
import {
  UPLOADING_MATERIAL_PHONE_MAXSIZE,
  UPLOADING_MATERIAL_PC_MAXSIZE,
  ADD_MATERIAL_ICON,
  ACCEPT_MIME_TYPES,
} from '@/composable/constant_common';
import { onsAlert } from '@/composable/onsen_common';

// 入力を許可するファイル種類
const FILE_INPUT_ACCEPT = ACCEPT_MIME_TYPES.join(', ');

defineProps({
  getMaterial: Function,
  resetPollingInterval: Function,
});

// メディアアップロードモーダル可視状態
const isModalVisible = ref(false);
// サーキュラー可視状態
const isProcessWait = ref(false);
// 素材ファイルリスト
const materialFileList = ref([]);
// アップロード素材表示オブジェクト
const uploadingMaterialObject = ref({
  materialUrl: '',
  src: '',
  imageFlag: false,
  poster: '',
  fileSize: 0,
  duration: 0,
});
// アップロード素材表示オブジェクトのリスト
const uploadingMaterialObjectList = ref([]);
// uploadingMaterialObjectList詰め込み時の一時格納用リスト
const inputArray = ref([]);
// 容量超過素材リスト
const overCapacityMaterialList = ref([]);
// スマホアラートフラグ
const smartPhoneAlertFlag = ref(0);
// PCアラートフラグ
const pcAlertFlag = ref(0);
// 合計ファイル数
const totalFileLength = ref(0);
// 合計ファイルサイズ
const totalFileSize = ref(0);

// ファイル入力の参照
const fileInputRef = ref(null);

// モーダル表示状態の監視
watch(isModalVisible, (newValue) => {
  if (newValue) {
    disableScroll();
  } else {
    enableScroll();
  }
});

// ファイルリセット処理
const resetFile = () => {
  fileInputRef.value.value = null;
  URL.revokeObjectURL(uploadingMaterialObject.value.icon);
  uploadingMaterialObjectList.value = [];
};

// サムネイル作成
const createThumbnails = (src, i) => {
  const video = document.createElement('video');
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  //  読み込みが完了したらcanvas サイズを設定
  video.onloadedmetadata = () => {
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    video.currentTime = 0;
  };

  //  video.currentTime が変更されたらキャプチャ
  video.onseeked = () => {
    context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    uploadingMaterialObjectList.value[i].poster = canvas.toDataURL('image/jpeg');
    uploadingMaterialObjectList.value[i].duration = Math.round(video.duration);
  };

  // 動画を読み込む
  video.preload = 'metadata';
  video.src = src;
};

// モーダル表示フラグ変更処理
const updateIsMaterialUploadModalVisible = () => {
  if (isModalVisible.value) {
    resetFile();
  }
  isModalVisible.value = !isModalVisible.value;
};

// ファイル選択時の処理
const callOnFileChange = async (event) => {
  isProcessWait.value = true;

  // 初期化
  uploadingMaterialObject.value = {
    materialUrl: '',
    src: '',
    imageFlag: false,
    poster: '',
    fileSize: 0,
  };
  // スマートフォンのアラートフラグを初期化
  smartPhoneAlertFlag.value = 0;
  // PCのアラートフラグを初期化
  pcAlertFlag.value = 0;
  // 容量超過素材リストの初期化
  overCapacityMaterialList.value = [];
  // 複数素材情報格納用の配列を初期化
  uploadingMaterialObjectList.value = [];
  // 複数ファイル格納用の配列を初期化
  materialFileList.value = [];
  // デコードしたファイル情報格納用の配列を初期化
  inputArray.value = [];
  // ファイル数の初期化
  totalFileLength.value = 0;
  // ファイル合計数の初期化
  totalFileSize.value = 0;

  // ユーザが選択したファイル情報(event)をデコード
  const res = await onFileChange(event);
  inputArray.value = Array.isArray(res) ? res : [res];

  for (let i = 0; i < inputArray.value.length; i += 1) {
    if (isSmartPhone()) {
      // スマホ利用時のファイルサイズ制限フラグ
      if (inputArray.value[i].fileSize > UPLOADING_MATERIAL_PHONE_MAXSIZE) {
        smartPhoneAlertFlag.value += 1;
        // 容量超過したファイル名を格納
        overCapacityMaterialList.value[i] = inputArray.value[i].materialFile.name;
      }
      // PC利用時のファイルサイズ制限フラグ
    } else if (inputArray.value[i].fileSize > UPLOADING_MATERIAL_PC_MAXSIZE) {
      pcAlertFlag.value += 1;
      // 容量超過したファイル名を格納
      overCapacityMaterialList.value[i] = inputArray.value[i].materialFile.name;
    }
  }

  if (smartPhoneAlertFlag.value > 0 || pcAlertFlag.value > 0) {
    resetFile();
    isProcessWait.value = false;

    const overCapacityMaterial = overCapacityMaterialList.value
      .map((fileName) => `「${fileName}」`)
      .join('');
    let message = UPLOADING_MATERIAL_PHONE_MAXSIZE;
    if (pcAlertFlag.value > 0) {
      message = UPLOADING_MATERIAL_PC_MAXSIZE;
    }

    // スマホ利用時のファイルサイズ制限
    onsAlert(`${overCapacityMaterial}が${message}MBを超えています。選択し直してください。`);
    return;
  }

  // 各ファイル情報をリストに格納
  for (let i = 0; i < inputArray.value.length; i += 1) {
    const material = inputArray.value[i];
    uploadingMaterialObject.value = {
      ...uploadingMaterialObject.value,
      materialUrl: material.icon,
      src: material.src,
      imageFlag: material.imageFlag,
      fileSize: material.fileSize,
    };

    // サムネイルおよび再生時間を取得
    createThumbnails(uploadingMaterialObject.value.src, i);

    // 出力リストの作成
    uploadingMaterialObjectList.value[i] = uploadingMaterialObject.value;
    materialFileList.value[i] = material.materialFile;

    // 合計ファイルサイズ
    totalFileSize.value += uploadingMaterialObjectList.value[i].fileSize;
  }

  isProcessWait.value = false;
  updateIsMaterialUploadModalVisible();
  // 合計ファイル数
  totalFileLength.value = materialFileList.value.length;
};

// ファイル入力をトリガー
const triggerFileInput = () => {
  fileInputRef.value.click();
};
</script>

<style scoped>
.material-add-button {
  width: 100%;
  height: 100%;
  padding: 0;
  border: 1px;
  border-color: white;
}
.add-icon {
  width: 100%;
  aspect-ratio: 1;
  vertical-align: top;
  object-fit: cover;
}
input[type='file'] {
  display: none;
}
.modal-wrapper {
  height: 100vh;
  height: 100dvh;
}
.progress-circular-wrapper {
  background-color: rgba(0, 0, 0, 0.7);
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 10000;
}
</style>
